import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  deleteForumComment,
  deleteForumPost,
  deleteJobPost,
  getAvailableDoctors,
  getForumComments,
  getForumPosts,
  getJobPosts,
  updateForumComment,
  updateForumPost,
  updateJobPost,
} from "../../api/axios";

const POST_TYPES = [
  { value: "question", label: "Question" },
  { value: "case_report", label: "Case report" },
  { value: "image", label: "Image" },
  { value: "discussion", label: "Discussion" },
];

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-6 my-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-[#00342b]">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function EditPostModal({ post, onClose, onSaved }) {
  const [form, setForm] = useState({ title: post.title, type: post.type, content: post.content, tags: post.tags || "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateForumPost(post.id, form);
      toast.success("Post updated");
      onSaved();
    } catch (err) {
      toast.error(err.message || "Failed to update post");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Edit Post" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <select className="w-full h-11 border rounded-xl px-3 text-sm" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
          {POST_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <input className="w-full h-11 border rounded-xl px-3 text-sm" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" required />
        <textarea className="w-full min-h-40 border rounded-xl px-3 py-2 text-sm" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Content" required />
        <input className="w-full h-11 border rounded-xl px-3 text-sm" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="Tags (comma separated)" />
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl border text-gray-600">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-xl bg-[#00342b] text-white font-semibold disabled:opacity-60">{saving ? "Saving…" : "Save Changes"}</button>
        </div>
      </form>
    </Modal>
  );
}

function EditJobModal({ job, onClose, onSaved }) {
  const [form, setForm] = useState({
    post_name: job.post_name,
    job_location: job.job_location,
    hospital_name: job.hospital_name,
    vacancy_available: job.vacancy_available,
    description: job.description,
    status: job.status,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateJobPost(job.id, form);
      toast.success("Job post updated");
      onSaved();
    } catch (err) {
      toast.error(err.message || "Failed to update job post");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Edit Job Post" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <input className="w-full h-11 border rounded-xl px-3 text-sm" value={form.post_name} onChange={(e) => setForm({ ...form, post_name: e.target.value })} placeholder="Job title" required />
        <div className="grid grid-cols-2 gap-3">
          <input className="h-11 border rounded-xl px-3 text-sm" value={form.hospital_name} onChange={(e) => setForm({ ...form, hospital_name: e.target.value })} placeholder="Hospital" required />
          <input className="h-11 border rounded-xl px-3 text-sm" value={form.job_location} onChange={(e) => setForm({ ...form, job_location: e.target.value })} placeholder="Location" required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input type="number" min="1" className="h-11 border rounded-xl px-3 text-sm" value={form.vacancy_available} onChange={(e) => setForm({ ...form, vacancy_available: e.target.value })} placeholder="Vacancies" required />
          <select className="h-11 border rounded-xl px-3 text-sm bg-white" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="hidden">Hidden</option>
          </select>
        </div>
        <textarea className="w-full min-h-32 border rounded-xl px-3 py-2 text-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" required />
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl border text-gray-600">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-xl bg-[#00342b] text-white font-semibold disabled:opacity-60">{saving ? "Saving…" : "Save Changes"}</button>
        </div>
      </form>
    </Modal>
  );
}

export default function YourPosts() {
  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [replyMap, setReplyMap] = useState({});
  const [editingPost, setEditingPost] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState("");

  const load = async () => {
    const [p, c, j, a] = await Promise.all([
      getForumPosts({ mine: "1" }),
      getForumComments("0", true).catch(() => ({ data: [] })),
      getJobPosts({ mine: "1" }),
      getAvailableDoctors({ mine: "1" }),
    ]);
    const myPosts = p.data || [];
    setPosts(myPosts);
    setComments(c.data || []);
    setJobs(j.data || []);
    setAvailability(a.data || []);

    const replies = {};
    await Promise.all(
      myPosts.map(async (post) => {
        const res = await getForumComments(post.id).catch(() => ({ data: [] }));
        replies[post.id] = res.data || [];
      }),
    );
    setReplyMap(replies);
  };

  useEffect(() => { load(); }, []);

  const handleDeletePost = async (post) => {
    if (!window.confirm(`Delete "${post.title}" permanently? This cannot be undone.`)) return;
    try {
      await deleteForumPost(post.id);
      toast.success("Post deleted");
      load();
    } catch (err) {
      toast.error(err.message || "Failed to delete post");
    }
  };

  const handleDeleteJob = async (job) => {
    if (!window.confirm(`Delete "${job.post_name}" permanently? This cannot be undone.`)) return;
    try {
      await deleteJobPost(job.id);
      toast.success("Job post deleted");
      load();
    } catch (err) {
      toast.error(err.message || "Failed to delete job post");
    }
  };

  const handleDeleteComment = async (comment) => {
    if (!window.confirm("Delete this comment permanently?")) return;
    try {
      await deleteForumComment(comment.id);
      toast.success("Comment deleted");
      load();
    } catch (err) {
      toast.error(err.message || "Failed to delete comment");
    }
  };

  const startEditComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentText(comment.comment);
  };

  const saveEditComment = async () => {
    if (!editingCommentText.trim()) { toast.error("Comment can't be empty"); return; }
    try {
      await updateForumComment(editingCommentId, editingCommentText);
      toast.success("Comment updated");
      setEditingCommentId(null);
      load();
    } catch (err) {
      toast.error(err.message || "Failed to update comment");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
      <section className="bg-white border rounded-3xl p-6">
        <h1 className="text-2xl font-extrabold text-[#00342b] mb-4">Your Posts</h1>
        <div className="grid gap-3">
          {posts.map((p) => (
            <div key={p.id} className="border rounded-2xl p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <Link to={`/forum/${p.id}`} className="block min-w-0">
                  <div className="font-semibold">{p.title}</div>
                  <div className="text-sm text-gray-500">
                    {p.type} • {p.comment_count || 0} replies • {p.views || 0} views • {p.like_count || 0} likes
                  </div>
                </Link>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => setEditingPost(p)} className="px-3 py-1.5 rounded-lg border text-xs font-semibold text-gray-600 hover:bg-gray-50">Edit</button>
                  <button onClick={() => handleDeletePost(p)} className="px-3 py-1.5 rounded-lg border border-red-200 bg-red-50 text-red-700 text-xs font-semibold hover:bg-red-100">Delete</button>
                </div>
              </div>
              <div className="space-y-2">
                {(replyMap[p.id] || []).slice(0, 3).map((reply) => (
                  <div key={reply.id} className="rounded-xl bg-gray-50 border p-3">
                    <div className="text-xs uppercase tracking-[0.2em] text-gray-500">
                      {reply.author_name}
                    </div>
                    <div className="text-sm text-gray-700 mt-1">{reply.comment}</div>
                  </div>
                ))}
                {!(replyMap[p.id] || []).length && (
                  <div className="text-sm text-gray-500">No replies yet.</div>
                )}
              </div>
            </div>
          ))}
          {!posts.length && <p className="text-gray-500">No posts yet.</p>}
        </div>
      </section>

      <section className="bg-white border rounded-3xl p-6">
        <h2 className="text-xl font-bold text-[#00342b] mb-4">Your Comments</h2>
        <div className="grid gap-3">
          {comments.map((c) => (
            <div key={c.id} className="border rounded-2xl p-4">
              <div className="flex items-start justify-between gap-3">
                <Link to={`/forum/${c.post_id}`} className="text-sm text-gray-500 hover:text-[#00342b] hover:underline">
                  On "{c.post_title || `post #${c.post_id}`}"
                </Link>
                {editingCommentId !== c.id && (
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => startEditComment(c)} className="px-3 py-1.5 rounded-lg border text-xs font-semibold text-gray-600 hover:bg-gray-50">Edit</button>
                    <button onClick={() => handleDeleteComment(c)} className="px-3 py-1.5 rounded-lg border border-red-200 bg-red-50 text-red-700 text-xs font-semibold hover:bg-red-100">Delete</button>
                  </div>
                )}
              </div>
              {editingCommentId === c.id ? (
                <div className="mt-2 space-y-2">
                  <textarea className="w-full min-h-20 border rounded-xl px-3 py-2 text-sm" value={editingCommentText} onChange={(e) => setEditingCommentText(e.target.value)} />
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setEditingCommentId(null)} className="px-3 py-1.5 rounded-lg border text-xs font-semibold text-gray-600">Cancel</button>
                    <button onClick={saveEditComment} className="px-3 py-1.5 rounded-lg bg-[#00342b] text-white text-xs font-semibold">Save</button>
                  </div>
                </div>
              ) : (
                <div className="mt-1">{c.comment}</div>
              )}
            </div>
          ))}
          {!comments.length && <p className="text-gray-500">No comments yet.</p>}
        </div>
      </section>

      <section className="bg-white border rounded-3xl p-6">
        <h2 className="text-xl font-bold text-[#00342b] mb-4">Your Jobs</h2>
        <div className="grid gap-3">
          {jobs.map((j) => (
            <div key={j.id} className="border rounded-2xl p-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold">{j.post_name}</div>
                <div className="text-sm text-gray-500">{j.hospital_name} • {j.job_location} • {j.vacancy_available} openings • {j.status}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setEditingJob(j)} className="px-3 py-1.5 rounded-lg border text-xs font-semibold text-gray-600 hover:bg-gray-50">Edit</button>
                <button onClick={() => handleDeleteJob(j)} className="px-3 py-1.5 rounded-lg border border-red-200 bg-red-50 text-red-700 text-xs font-semibold hover:bg-red-100">Delete</button>
              </div>
            </div>
          ))}
          {!jobs.length && <p className="text-gray-500">No job posts yet.</p>}
        </div>
      </section>

      <section className="bg-white border rounded-3xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#00342b]">Your Availability</h2>
          <Link to="/jobs/available" className="text-sm font-semibold text-[#00342b] underline">Manage availability</Link>
        </div>
        <div className="grid gap-3">
          {availability.map((a) => (
            <div key={a.id} className="border rounded-2xl p-4">
              <div className="font-semibold">{a.specialty || "Availability profile"}</div>
              <div className="text-sm text-gray-500">{a.location || "No location"}</div>
            </div>
          ))}
          {!availability.length && <p className="text-gray-500">No availability profile yet.</p>}
        </div>
      </section>

      {editingPost && <EditPostModal post={editingPost} onClose={() => setEditingPost(null)} onSaved={() => { setEditingPost(null); load(); }} />}
      {editingJob && <EditJobModal job={editingJob} onClose={() => setEditingJob(null)} onSaved={() => { setEditingJob(null); load(); }} />}
    </div>
  );
}
